import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Timer, Microscope } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmKpi, Pill, statusTone, GOLD, MUTED, NAVY, RED, AMBER, GREEN, FIELD } from "@/components/om/OmBits";

const COLUMNS = ["Open", "Assigned", "In Progress", "Resolved", "Closed", "Verified"];
const NEXT = { Open: "Assigned", Assigned: "In Progress", "In Progress": "Resolved", Resolved: "Closed", Closed: "Verified" };
const BLANK = { asset_id: "", description: "", fault_category: "Mechanical", severity: "Medium" };

export default function OmCorrective() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [sel, setSel] = useState(null);
  const [rca, setRca] = useState(null);
  const [rcaBusy, setRcaBusy] = useState(false);

  const load = () => api.get("/om/tickets").then(({ data }) => setTickets(data));
  useEffect(() => {
    load();
    api.get("/om/assets").then(({ data }) => setAssets(data));
    api.get("/om/meta").then(({ data }) => setMeta(data));
  }, []);

  const stats = useMemo(() => {
    const openT = tickets.filter((t) => !["Closed", "Verified"].includes(t.status));
    const resolved = tickets.filter((t) => t.repair_hours);
    return {
      open: openT.length,
      critical: openT.filter((t) => t.severity === "Critical").length,
      breached: tickets.filter((t) => t.sla_breached).length,
      chronic: tickets.filter((t) => t.chronic).length,
      sla: tickets.length ? Math.round((tickets.filter((t) => !t.sla_breached).length / tickets.length) * 100) : 100,
      mttr: resolved.length ? (resolved.reduce((s, t) => s + t.repair_hours, 0) / resolved.length).toFixed(1) : 0,
    };
  }, [tickets]);

  const create = async () => {
    try {
      await api.post("/om/tickets", form);
      toast.success("Fault ticket raised — SLA clock started");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const advance = async (t, status) => {
    try {
      await api.put(`/om/tickets/${t.id}`, {
        status,
        assignee: t.assignee || "",
        root_cause: t.root_cause || "",
        corrective_action: t.corrective_action || "",
        repair_hours: Number(t.repair_hours || 0),
      });
      toast.success(`${t.code} · ${status}`);
      setSel(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const slaTone = (t) => t.sla_breached ? RED : t.sla_used_pct >= 75 ? AMBER : GREEN;

  const openTicket = (t) => {
    setSel({ ...t });
    setRca(null);
    api.get(`/om/rca/by-ticket/${t.id}`).then(({ data }) => setRca(data?.id ? data : null)).catch(() => setRca(null));
  };

  const generateRca = async () => {
    setRcaBusy(true);
    try {
      const { data } = await api.post("/om/rca/generate", { ticket_id: sel.id });
      toast.success(data.template_matched ? `RCA drafted from template “${data.fault_name}”` : "RCA scaffold created — complete the Whys");
      navigate(`/om/rca/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRcaBusy(false); }
  };

  return (
    <div data-testid="om-corrective-page">
      <OmHead title="Corrective Maintenance" subtitle="Fault tickets with severity-based SLA clocks — Critical 4h, High 24h, Medium 72h, Low 7d.">
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-cm-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Raise CM ticket
        </Button>
      </OmHead>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-3">
        <OmKpi label="Open tickets" value={stats.open} testid="kpi-cm-open" />
        <OmKpi label="Open critical" value={stats.critical} tone={stats.critical ? RED : GREEN} testid="kpi-cm-critical" />
        <OmKpi label="SLA compliance" value={`${stats.sla}%`} testid="kpi-cm-sla" />
        <OmKpi label="SLA breaches" value={stats.breached} tone={stats.breached ? RED : GREEN} />
        <OmKpi label="Chronic faults" value={stats.chronic} tone={stats.chronic ? AMBER : GREEN} />
        <OmKpi label="MTTR (hours)" value={stats.mttr} />
      </div>

      <div className="grid md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="om-cm-board">
        {COLUMNS.map((col) => {
          const items = tickets.filter((t) => t.status === col);
          return (
            <div key={col}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10.5px] uppercase tracking-[0.12em]" style={{ color: GOLD }}>{col}</span>
                <span className="text-[11px] tabular-nums" style={{ color: MUTED }}>{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((t) => (
                  <button key={t.id} onClick={() => openTicket(t)} className="w-full text-left border p-2.5 transition-transform duration-150 hover:-translate-y-0.5"
                    style={{ background: "#112240", borderColor: t.severity === "Critical" ? `${RED}55` : "rgba(201,168,76,0.16)", borderRadius: 8 }}
                    data-testid={`om-cm-card-${t.code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10.5px] tabular-nums" style={{ color: MUTED }}>{t.code}</span>
                      <Pill tone={statusTone(t.severity)}>{t.severity}</Pill>
                    </div>
                    <p className="text-white text-[12px] mt-1.5 leading-snug line-clamp-3">{t.description}</p>
                    <div className="text-[10.5px] mt-1.5" style={{ color: MUTED }}>{t.asset_code} · {t.asset_name}</div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <Timer className="w-3 h-3" style={{ color: slaTone(t) }} />
                      <div className="flex-1 h-1" style={{ background: "rgba(255,255,255,0.1)", borderRadius: 999 }}>
                        <div style={{ width: `${Math.min(100, t.sla_used_pct)}%`, height: "100%", background: slaTone(t), borderRadius: 999 }} />
                      </div>
                      <span className="text-[10px] tabular-nums" style={{ color: slaTone(t) }}>
                        {t.sla_breached ? "breached" : `${t.hours_remaining}h left`}
                      </span>
                    </div>
                    {t.chronic ? <div className="mt-1.5"><Pill tone="warn">chronic — repeat within 30 days</Pill></div> : null}
                    {t.rca_status ? <div className="mt-1.5"><Pill tone={t.rca_status === "Approved" ? "good" : "gold"}>RCA {t.rca_status}</Pill></div> : null}
                  </button>
                ))}
                {items.length === 0 ? <p className="text-[11px] px-1" style={{ color: MUTED }}>—</p> : null}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">Raise a fault ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Asset</Label>
              <select value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-cm-asset-select">
                <option value="">Select asset</option>
                {assets.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["fault_category", "Fault category", meta?.fault_categories], ["severity", "Severity", meta?.severities]].map(([k, l, opts]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                  <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid={`om-cm-${k}-select`}>
                    {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Fault description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={FIELD} data-testid="om-cm-description-input" />
            </div>
            <p className="text-[11px]" style={{ color: MUTED }}>Critical and High severity notify the O&amp;M Manager and Site Head immediately.</p>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!form.asset_id || !form.description} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-cm-save-btn">Raise ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }} data-testid="om-cm-dialog">
          <DialogHeader><DialogTitle className="text-white">{sel?.code} · {sel?.asset_name}</DialogTitle></DialogHeader>
          {sel ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Pill tone={statusTone(sel.severity)}>{sel.severity}</Pill>
                <Pill tone={statusTone(sel.status)}>{sel.status}</Pill>
                <Pill tone={sel.sla_breached ? "bad" : sel.sla_used_pct >= 75 ? "warn" : "good"}>
                  SLA {sel.sla_hours}h · {sel.sla_used_pct}% used
                </Pill>
              </div>
              <p className="text-white text-[13px]">{sel.description}</p>
              <div className="text-[11.5px]" style={{ color: MUTED }}>
                Raised by {sel.reported_by} on {new Date(sel.created_at).toLocaleString("en-IN")} · {sel.fault_category}
              </div>
              {[["assignee", "Assigned technician", "text"], ["root_cause", "Root cause", "text"],
                ["corrective_action", "Corrective action", "text"], ["repair_hours", "Actual repair time (h)", "number"]].map(([k, l, t]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                  <Input type={t} value={sel[k] || ""} onChange={(e) => setSel({ ...sel, [k]: e.target.value })} style={FIELD} data-testid={`om-cm-${k}-input`} />
                </div>
              ))}
              <div className="border-t pt-3" style={{ borderColor: `${GOLD}26` }} data-testid="om-cm-rca-tab">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Microscope className="w-3.5 h-3.5" style={{ color: GOLD }} />
                      <span className="text-white text-[13px] font-medium">Root cause analysis</span>
                      {rca ? <Pill tone={rca.status === "Approved" ? "good" : "gold"}>{rca.status}</Pill> : null}
                    </div>
                    <p className="text-[11.5px] mt-1" style={{ color: MUTED }}>
                      {rca ? `${rca.fault_name} · ${rca.template_matched ? "template match" : "manual scaffold"}`
                        : "No RCA yet. Critical tickets draft one automatically."}
                    </p>
                  </div>
                  {rca ? (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/om/rca/${rca.id}`)}
                      style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-cm-view-rca-btn">
                      View RCA
                    </Button>
                  ) : ["om_manager", "pm", "hod", "admin", "superadmin"].includes(meta?.role) ? (
                    <Button size="sm" disabled={rcaBusy} onClick={generateRca} className="font-semibold"
                      style={{ background: GOLD, color: NAVY }} data-testid="om-cm-generate-rca-btn">
                      Generate RCA
                    </Button>
                  ) : null}
                </div>
              </div>

            </div>
          ) : null}
          <DialogFooter>
            {sel && NEXT[sel.status] ? (
              <Button onClick={() => advance(sel, NEXT[sel.status])} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-cm-advance-btn">
                Move to {NEXT[sel.status]}
              </Button>
            ) : <span className="text-[12px]" style={{ color: GREEN }}>Ticket closed and verified.</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
